/**
 * Viewport buckets the API will serve a per-view consent body for. Named after, and bounded by,
 * the same Tailwind breakpoints this app's CSS uses, so the body an admin authored for `md` is the
 * one shown at the width `md:` utilities start applying at.
 */
export const SIGNABLE_DOCUMENT_VIEWS = ['sm', 'md', 'lg', 'xl'] as const;

export type SignableDocumentView = (typeof SIGNABLE_DOCUMENT_VIEWS)[number];

/** Tailwind's breakpoint minimums, largest first so the first match wins. */
const BREAKPOINTS: { minWidth: number; view: SignableDocumentView }[] = [
  { minWidth: 1280, view: 'xl' },
  { minWidth: 1024, view: 'lg' },
  { minWidth: 768, view: 'md' },
  { minWidth: 0, view: 'sm' }
];

/**
 * The view bucket the current viewport falls into.
 *
 * Returns `sm` below the `md` breakpoint rather than having a separate "extra small" bucket,
 * matching the four views the admin editor offers.
 *
 * Safe to call during SSR or in a test without a window: falls back to `undefined`, and the API
 * then serves the canonical body — which is what every caller got before per-view bodies existed.
 */
export const getSignableDocumentView = (): SignableDocumentView | undefined => {
  if (typeof window === 'undefined') return undefined;

  const width = window.innerWidth;
  if (!Number.isFinite(width) || width <= 0) return undefined;

  return BREAKPOINTS.find((breakpoint) => width >= breakpoint.minWidth)?.view;
};
